import React, {useRef, useState} from 'react';
import {Platform, View, StyleProp, ViewStyle} from 'react-native';
import {WebView as RNWebView} from 'react-native-webview';
import OriginalAutoHeightWebView from 'react-native-autoheight-webview';

interface AutoHeightWebViewProps {
  source: {html: string; baseUrl?: string};
  style?: StyleProp<ViewStyle>;
  scrollEnabled?: boolean;
  onShouldStartLoadWithRequest?: (event: any) => boolean;
  injectedJavaScript?: string;
  originWhitelist?: string[];
  javaScriptEnabled?: boolean;
}

const isWeb = Platform.OS === 'web';

const AutoHeightWebView: React.FC<AutoHeightWebViewProps> = props => {
  const {
    source,
    style,
    scrollEnabled,
    onShouldStartLoadWithRequest,
    injectedJavaScript,
    originWhitelist,
    javaScriptEnabled = true,
  } = props;

  const [height, setHeight] = useState<number>(100);
  const webViewRef = useRef<any>(null);

  // Injected JavaScript for web platform to measure and update height
  const webHeightScript = `
    function updateHeight() {
      const height = document.body.scrollHeight;
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'height', height }));
    }
    
    // Initial height calculation
    updateHeight();
    
    // Update height on content changes
    const observer = new MutationObserver(updateHeight);
    observer.observe(document.body, { 
      attributes: true, 
      childList: true, 
      subtree: true 
    });
    
    // Update on window resize
    window.addEventListener('resize', updateHeight);
    
    // Update on image loads
    document.querySelectorAll('img').forEach(img => {
      img.addEventListener('load', updateHeight);
    });
    
    // Make external links open in a new tab
    document.addEventListener('click', function(e) {
      const target = e.target.closest('a');
      if (target && target.getAttribute('href') && target.getAttribute('href').startsWith('http')) {
        e.preventDefault();
        window.open(target.getAttribute('href'), '_blank');
      }
    });
    
    ${injectedJavaScript || ''}
  `;

  // Enhanced message handler with fallback and error handling
  const handleWebMessage = (event: any) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === 'height') {
        // Only update if height is reasonable
        if (data.height > 20 && data.height < 100000) {
          setHeight(data.height);
        }
      }
      // Handle custom URL event
      else if (
        data.type === 'url' &&
        data.url &&
        onShouldStartLoadWithRequest
      ) {
        onShouldStartLoadWithRequest({url: data.url});
      }
    } catch (error) {
    }
  };

  // Web implementation
  if (isWeb) {
    return (
      <View style={[style, {height}]}>
        <RNWebView
          ref={webViewRef}
          source={source}
          style={{height: '100%', width: '100%'}}
          scrollEnabled={scrollEnabled}
          originWhitelist={originWhitelist || ['*']}
          javaScriptEnabled={javaScriptEnabled}
          injectedJavaScript={webHeightScript}
          onMessage={handleWebMessage}
          // Handling URL requests on web
          onShouldStartLoadWithRequest={event => {
            if (onShouldStartLoadWithRequest && event.url !== 'about:blank') {
              return onShouldStartLoadWithRequest(event);
            }
            return true;
          }}
        />
      </View>
    );
  }

  // React Native implementation (use original AutoHeightWebView)
  return <OriginalAutoHeightWebView {...props} style={style} />;
};

export default AutoHeightWebView;
